import type { ProjectFormService } from 'features/project-form/service/service'
import type {
  ProjectValidateSourcesPayload,
  ValidatePhaseEvent,
} from 'features/project-form/types'

type Args = {
  service: ProjectFormService
  payload: ProjectValidateSourcesPayload
  onPhase: (event: ValidatePhaseEvent) => void
}

/**
 * Drive the streaming scope-validate endpoint and surface each PhaseEvent
 * to the caller via `onPhase`. On the terminal `done` frame this resolves
 * (success or validation errors, matching the legacy 200/422 shape via a
 * thrown Error so existing `.response.data.detail` catches still work).
 * On the terminal `error` frame, throws an Error with the server-side detail.
 */
export async function validateProjectSourcesWithProgress({
  service,
  payload,
  onPhase,
}: Args): Promise<void> {
  for await (const event of service.validateProjectSourcesStream(payload)) {
    onPhase(event)
    if (event.phase === 'done') {
      const result = event.result
      if (result && result.status >= 400) {
        const err = new Error(result.detail) as Error & {
          response: { data: { detail: string } }
        }
        err.response = { data: { detail: result.detail } }
        throw err
      }
      return
    }
    if (event.phase === 'error') {
      const detail = event.result?.detail ?? 'Validation failed.'
      const err = new Error(detail) as Error & {
        response: { data: { detail: string } }
      }
      err.response = { data: { detail } }
      throw err
    }
  }
  throw new Error('Validation stream ended without a terminal event.')
}
